<template>
  <!-- Combobox ARIA: buscador + listbox siempre desplegado (va dentro de un popover, una subvista o la hoja).
       ↑ ↓ Inicio Fin recorren las opciones activas, Intro elige, Esc emite `close` y corta la propagación. -->
  <div class="pk" @keydown="onKey">
    <input ref="inputRef" v-model="query" class="in pk-in" type="text" role="combobox" autocomplete="off"
           aria-autocomplete="list" aria-expanded="true" :aria-controls="listId" :aria-label="placeholder || t('pk.search')"
           :aria-activedescendant="activeIdx >= 0 ? optId(activeIdx) : undefined" :placeholder="placeholder || t('pk.search')" />
    <ul :id="listId" ref="listRef" class="pk-list" role="listbox" :aria-label="placeholder || t('pk.search')">
      <template v-for="(o, i) in shown" :key="o.group + '|' + o.value">
        <li v-if="o.group && (i === 0 || shown[i - 1].group !== o.group)" class="pk-group" role="presentation">{{ o.group }}</li>
        <li :id="optId(i)" class="pk-opt" role="option" :class="{ active: i === activeIdx, off: o.disabled }"
            :aria-selected="i === activeIdx" :aria-disabled="o.disabled ? 'true' : undefined" :title="o.hint || undefined"
            @mousedown.prevent @mousemove="o.disabled || (activeIdx = i)" @click="choose(o)">
          <span class="pk-main">
            <span class="pk-label">{{ o.label }}</span>
            <span v-if="o.sub" class="pk-sub mono">{{ o.sub }}</span>
            <span v-if="o.hint" class="pk-hint">{{ o.hint }}</span>
          </span>
          <span v-if="o.meta" class="pk-meta">{{ o.meta }}</span>
        </li>
      </template>
      <li v-if="!shown.length" class="pk-empty" role="presentation">{{ emptyText || t('pk.none') }}</li>
    </ul>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { t } from '@vem/i18n.js';

let seq = 0;

const props = defineProps({
  /** [{value, label, sub, meta, group, disabled, hint}] en el orden en que se muestran (los grupos, consecutivos). */
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
  emptyText: { type: String, default: '' },
});
const emit = defineEmits(['pick', 'close']);

const listId = `pk-list-${++seq}`;
const query = ref('');
const activeIdx = ref(-1);
const inputRef = ref(null);
const listRef = ref(null);

const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const shown = computed(() => {
  const q = fold(query.value.trim());
  if (!q) return props.options;
  return props.options.filter((o) => fold(`${o.label} ${o.sub || ''} ${o.meta || ''} ${o.value}`).includes(q));
});

const optId = (i) => `${listId}-${i}`;

function firstEnabled(from, step) {
  const list = shown.value;
  for (let i = from; i >= 0 && i < list.length; i += step) {
    if (!list[i].disabled) return i;
  }
  return -1;
}

watch(shown, () => {
  activeIdx.value = firstEnabled(0, 1);
}, { immediate: true });

watch(activeIdx, (i) => {
  if (i < 0) return;
  nextTick(() => document.getElementById(optId(i))?.scrollIntoView({ block: 'nearest' }));
});

function choose(o) {
  if (o && !o.disabled) emit('pick', o.value);
}

function move(step) {
  const next = firstEnabled(activeIdx.value + step, step);
  if (next >= 0) activeIdx.value = next;
}

function onKey(e) {
  if (e.key === 'ArrowDown') move(1);
  else if (e.key === 'ArrowUp') move(-1);
  else if (e.key === 'Home' && e.ctrlKey) activeIdx.value = firstEnabled(0, 1);
  else if (e.key === 'End' && e.ctrlKey) activeIdx.value = firstEnabled(shown.value.length - 1, -1);
  else if (e.key === 'Enter') choose(shown.value[activeIdx.value]);
  else if (e.key === 'Escape') emit('close');
  else return;
  e.preventDefault();
  e.stopPropagation();
}

const focus = () => inputRef.value?.focus();

onMounted(() => {
  if (props.autofocus) nextTick(focus);
});

defineExpose({ focus });
</script>

<style>
.vem-root .pk { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.vem-root .pk-in { width: 100%; min-height: 32px; padding: 5px 9px; }
.vem-root .pk-list { list-style: none; margin: 0; padding: 0; max-height: 280px; overflow-y: auto; }
.vem-root .pk-group {
  font-family: var(--mono); font-size: 9.5px; letter-spacing: .12em; text-transform: uppercase; color: var(--tx3);
  padding: 8px 8px 3px;
}
.vem-root .pk-opt {
  display: flex; align-items: flex-start; gap: 8px; padding: 5px 8px; border-radius: 7px; cursor: pointer;
  font-size: 12.5px; line-height: 1.35; color: var(--tx);
}
.vem-root .pk-opt.active { background: var(--mint-bg); box-shadow: inset 2px 0 0 var(--mint); }
.vem-root .pk-opt.off { color: var(--tx3); cursor: default; }
.vem-root .pk-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.vem-root .pk-label { overflow-wrap: anywhere; }
.vem-root .pk-sub { font-size: 10.5px; color: var(--tx3); overflow-wrap: anywhere; }
.vem-root .pk-hint { font-size: 11px; color: var(--amber); }
.vem-root .pk-opt.off .pk-hint { color: var(--tx3); }
.vem-root .pk-meta { flex: 0 0 auto; font-family: var(--mono); font-size: 10.5px; color: var(--tx3); white-space: nowrap; padding-top: 1px; }
.vem-root .pk-empty { padding: 8px; color: var(--tx3); font-size: 12px; }
</style>
